import React, { useMemo, useState } from 'react'
import { DEALS, dateFromDaysAgo, money, type Deal } from './crmData'
import { useCrm } from './useCrm'
import { fetchAllDeals } from './crmApi'
import { Kpi, InitialBox, Pill, CrmToaster, SearchInput } from './crmUi'
import { Metric } from './Metric'
import { DealDetail } from './DealDetail'
import { LeadRedirect } from './CrmLeadPage'

/**
 * Billing — customers grouped by billing state, all from the Board. Tabs:
 * Success (paying, healthy), Payment Failed (card bouncing), Card Removed
 * (auto-cancelled). Rows open the full deal detail.
 */

type TabKey = 'success' | 'failed' | 'removed'
const isSuccess = (d: Deal) => d.stage === 'successfully_signed_up' && !d.paymentFailed && !d.cardRemoved
const isFailed = (d: Deal) => !!d.paymentFailed
const isRemoved = (d: Deal) => !!d.cardRemoved

const TABS: { key: TabKey; label: string; match: (d: Deal) => boolean; accent: string }[] = [
  { key: 'success', label: 'Success', match: isSuccess, accent: 'text-success' },
  { key: 'failed', label: 'Payment Failed', match: isFailed, accent: 'text-amber-600' },
  { key: 'removed', label: 'Card Removed', match: isRemoved, accent: 'text-rose-600' },
]

const sum = (list: Deal[], f: (d: Deal) => number) => list.reduce((a, d) => a + (f(d) || 0), 0)

export const CrmBilling: React.FC = () => {
  const { data: deals, reload } = useCrm(() => fetchAllDeals({ includeTerminal: true }) as Promise<Deal[]>, DEALS)
  const [tab, setTab] = useState<TabKey>('success')
  const [openDeal, setOpenDeal] = useState<Deal | null>(null)
  const [q, setQ] = useState('')

  const groups = useMemo(
    () => ({
      success: deals.filter(isSuccess),
      failed: deals.filter(isFailed),
      removed: deals.filter(isRemoved),
    }),
    [deals],
  )

  const healthyMrr = sum(groups.success, (d) => d.mrr || 0)
  const atRiskMrr = sum(groups.failed, (d) => d.paymentFailedAmount || d.mrr || 0)
  const lostMrr = sum(groups.removed, (d) => d.mrr || 0)

  const needle = q.trim().toLowerCase()
  const rows = groups[tab].filter((d) => !needle || `${d.company} ${d.name} ${d.contact || ''} ${d.plan || ''}`.toLowerCase().includes(needle))

  return (
    <div className="h-full flex flex-col bg-surface-secondary">
      <CrmToaster />

      <div className="px-6 pt-6 pb-4 bg-surface border-b border-border flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-txt-primary">Billing</h1>
        </div>
        <SearchInput value={q} onChange={setQ} placeholder="Search companies…" className="w-64" />
      </div>

      <div className="px-6 pt-4 bg-surface">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Paying accounts" value={groups.success.length} tone="green" />
          <Metric id="healthy_mrr" value={money(healthyMrr)} tone="green" />
          <Kpi label="At-risk MRR" value={money(atRiskMrr)} tone="amber" warn={groups.failed.length > 0} />
          <Kpi label="MRR at churn (card removed)" value={money(lostMrr)} tone="red" warn={groups.removed.length > 0} />
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 pt-3 bg-surface border-b border-border flex items-center gap-1 overflow-x-auto">
        {TABS.map((t) => {
          const active = t.key === tab
          const count = groups[t.key].length
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3.5 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                active ? 'border-brand-500 text-brand-600' : 'border-transparent text-txt-secondary hover:text-txt-primary'
              }`}
            >
              {t.label}
              <span className={`ml-2 text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${active ? 'bg-brand-100 text-brand-700' : 'bg-surface-tertiary text-txt-tertiary'}`}>
                {count}
              </span>
            </button>
          )
        })}
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-6">
        <div className="rounded-xl border border-border bg-surface overflow-hidden">
          {rows.length === 0 ? (
            <p className="text-sm text-txt-secondary py-16 text-center">
              {tab === 'success' ? 'No paying accounts yet' : tab === 'failed' ? 'No payment failures' : 'No card removals'}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary border-b border-border bg-surface-secondary">
                  <th className="py-2.5 px-4 font-medium">Company</th>
                  <th className="py-2.5 px-3 font-medium">Contact</th>
                  <th className="py-2.5 px-3 font-medium">Plan</th>
                  <th className="py-2.5 px-3 font-medium">MRR</th>
                  {tab === 'failed' && <th className="py-2.5 px-3 font-medium">Failed</th>}
                  {tab === 'failed' && <th className="py-2.5 px-3 font-medium">Attempt</th>}
                  {tab === 'removed' && <th className="py-2.5 px-3 font-medium">Removed</th>}
                  {tab === 'success' && <th className="py-2.5 px-3 font-medium">Customer since</th>}
                  <th className="py-2.5 px-4 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  // Fixtures are empty — show the live contact NAME from the deal (emails need the detail fetch).
                  const email = d.contact || null
                  return (
                    <tr key={d.id} onClick={() => setOpenDeal(d)} className="border-b border-border last:border-0 hover:bg-surface-hover cursor-pointer align-middle">
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <InitialBox name={d.company || d.name} logoUrl={d.logoUrl} />
                          <span className="font-medium text-txt-primary truncate">{d.company || d.name}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="text-txt-primary">{d.contact || '—'}</div>
                        {email && <span className="text-xs text-txt-tertiary truncate">{email}</span>}
                      </td>
                      <td className="py-2.5 px-3">{d.plan ? <Pill tone="blue">{d.plan}</Pill> : <span className="text-txt-tertiary">—</span>}</td>
                      <td className="py-2.5 px-3 text-txt-secondary whitespace-nowrap">{d.mrr ? `${money(d.mrr, d.country)}/mo` : '—'}</td>
                      {tab === 'failed' && <td className="py-2.5 px-3 text-amber-600 whitespace-nowrap">{d.paymentFailedDays != null ? `${d.paymentFailedDays}d ago` : '—'}</td>}
                      {tab === 'failed' && <td className="py-2.5 px-3 text-txt-secondary whitespace-nowrap">Attempt {d.paymentFailedAttempt || 1}</td>}
                      {tab === 'removed' && <td className="py-2.5 px-3 text-rose-600 whitespace-nowrap">{dateFromDaysAgo(d.cardRemovedDays ?? 0)}</td>}
                      {tab === 'success' && <td className="py-2.5 px-3 text-txt-tertiary whitespace-nowrap">{dateFromDaysAgo(d.createdDays)}</td>}
                      <td className="py-2.5 px-4 text-right"><span className="text-brand-600 text-xs font-semibold">Open →</span></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {openDeal && <LeadRedirect deal={openDeal} onDone={() => setOpenDeal(null)} />}
    </div>
  )
}
